import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// project imports
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

export default function ThemeCustomization({ children }) {
  const theme = Palette('light', 'default');

  const themeTypography = Typography(`'Manrope', sans-serif`);
  const themeCustomShadows = useMemo(() => CustomShadows(theme), [theme]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        } 
      },
      direction: 'ltr',
      mixins: {
        // 56, not 48: the header's Logo (components/logo/LogoMain.jsx) renders at
        // width:130px with no height constraint, which works out to ~42.5px tall
        // (its source PNG is 710x232) — plus this 12px of padding, that's ~54.5px
        // of actual required content height. A 48px floor let the real Toolbar
        // silently grow past it to fit the logo, while everything downstream that
        // assumed "the header is 48px" (the drawer's clearance margin, the sheet
        // bar/data rail height budgets, etc.) kept computing against the wrong,
        // smaller number. 56px is the first round number that's actually big
        // enough to contain the logo without overflowing, so the header's real
        // rendered height now matches what every other calculation assumes.
        toolbar: {
          minHeight: 56,
          paddingTop: 6,
          paddingBottom: 6
        }
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: themeTypography
    }),
    [theme, themeTypography, themeCustomShadows]
  );

  const themes = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
